import React from 'react'
import { AppService } from 'src/services'
import { FeedService } from 'src/services/feed.service'
import { Container } from 'typescript-ioc'
import { FeedDetailsView } from '@pushly/aqe/lib/components/feeds/feed-details-view'
import { Feed } from '@pushly/models/lib/structs/feeds/feed'
import { useActiveDomainChangeEffect } from '../../hooks/use-active-domain-change-effect'
import {
    confirmArchiveReferencedFeed,
    FeedJourneyReferenceEntry,
    getFeedJourneyReferences,
} from './feed-reference-guards'

export const FeedDetailsViewWrapper = (props: any) => {
    const appSvc: AppService = Container.get(AppService)
    const feedSvc: FeedService = Container.get(FeedService)

    const domainId = parseInt(props.match.params.domainId, 10)
    const feedId = parseInt(props.match.params.feedId, 10)

    // Resolved journey-usage data for this feed, sourced from the journeys-using-feed
    // seam. When the seam resolves `undefined` (request failure, unmappable payload, or
    // no referencing journeys) the "Used in Journeys" card is omitted (graceful
    // degradation) — no fabricated data is substituted.
    const [journeyReferences, setJourneyReferences] = React.useState<FeedJourneyReferenceEntry | undefined>()

    // The in-flight (or already settled) lookup behind `journeyReferences`, kept so the
    // archive guard can reuse this request instead of issuing a duplicate one.
    const journeyReferencesRequest = React.useRef<Promise<FeedJourneyReferenceEntry | undefined>>()

    React.useEffect(() => {
        let cancelled = false
        const request = getFeedJourneyReferences(domainId, feedId)
        journeyReferencesRequest.current = request
        request.then((references) => {
            if (!cancelled) {
                setJourneyReferences(references)
            }
        })
        return () => {
            cancelled = true
        }
    }, [domainId, feedId])

    const handleFeedClick = (notificationId: number) => {
        appSvc.routeWithinDomain(`/notifications/${notificationId}/summary`)
    }

    const handleFeedEdit = (feed: Feed) => {
        appSvc.route(`/domains/${domainId}/feeds/${feed.id}/edit`, { feed })
    }

    // View a referencing journey → the journey (campaign) detail page.
    const handleJourneyClick = (journeyId: number) => {
        appSvc.routeWithinDomain(`/journeys/${journeyId}`)
    }

    // Edit a referencing step. The Send Feed Notification step editor is owned by
    // the journey-feed-notification-step-ux spec and is not yet independently
    // routable (no step-edit deep-link/query param exists on the builder route).
    // Until that spec ships its routable target, deep-link into the journey builder
    // — the best available entry — so the author lands where that step is edited.
    // TODO(journey-feed-notification-step-ux): replace with the Send Feed
    // Notification step editor deep-link once that surface is routable by stepId.
    const handleStepEdit = (journeyId: number, _stepId: number) => {
        appSvc.routeWithinDomain(`/journeys/${journeyId}/builder`)
    }

    const archiveFeed = async (feed: Feed) => {
        const res = await feedSvc.archiveFeed(domainId, feed.id, { showLoadingScreen: true })
        if (res.ok) {
            appSvc.routeWithinDomain('/feeds')
        }
    }

    // Archive guard: resolve journey references,
    // then warn + list referencing journeys when the feed is referenced; otherwise
    // archive directly with no warning (graceful degradation).
    //
    // References come from the SAME lookup that feeds the "Used in Journeys" card
    // rather than a second request. Awaiting the mount promise covers the in-flight
    // case: reading the `journeyReferences` state instead would treat a request that
    // simply hadn't landed yet as "unreferenced" and silently skip the warning, which
    // is exactly the case the guard exists for. Awaiting an already-settled promise
    // costs nothing. A feed other than the one this view loaded (defensive — the
    // details view archives the feed it renders) gets its own scoped lookup.
    const handleFeedArchive = async (feed: Feed) => {
        const pendingRequest = feed.id === feedId ? journeyReferencesRequest.current : undefined
        const references = await (pendingRequest ?? getFeedJourneyReferences(domainId, feed.id))

        confirmArchiveReferencedFeed({
            feed,
            references,
            onConfirm: () => archiveFeed(feed),
        })
    }

    useActiveDomainChangeEffect((activeDomain, appService) => {
        appService.routeWithinDomain('/feeds')
    })

    return (
        <FeedDetailsView
            domainId={domainId}
            feedId={feedId}
            handleFeedClick={handleFeedClick}
            handleFeedEdit={handleFeedEdit}
            handleFeedArchive={handleFeedArchive}
            journeyReferences={journeyReferences}
            handleJourneyClick={handleJourneyClick}
            handleStepEdit={handleStepEdit}
        />
    )
}
